/**
 * Phase 7.7.3 — Contractor-facing contract UX model.
 * Maps backend statuses to friendly Persian labels without exposing engine jargon.
 */

export const CONTRACTOR_PROGRESS_STEPS = [
  { id: "profile", label: "اطلاعات پیمانکار" },
  { id: "review", label: "بررسی مدارک" },
  { id: "sign", label: "امضای قرارداد" },
  { id: "guarantee", label: "چک ضمانت" },
  { id: "active", label: "فعال شدن همکاری" },
];

export const CONTRACTOR_CONTRACT_PHASE = {
  EMPTY: "empty",
  PENDING_SIGNATURE: "pending_signature",
  WAITING_COMPLETION: "waiting_completion",
  AWAITING_GUARANTEE: "awaiting_guarantee",
  GUARANTEE_REVIEW: "guarantee_review",
  ACTIVE: "active",
  UNAVAILABLE: "unavailable",
};

const STATUS = {
  Draft: 1,
  Generated: 2,
  PendingContractorSignature: 3,
  ContractorSigned: 4,
  PendingSolarentSignature: 5,
  Signed: 6,
  Active: 7,
  Superseded: 8,
  Cancelled: 9,
  ApprovedForSignature: 10,
  AwaitingGuarantee: 11,
  Expired: 12,
  PendingReview: 13,
};

export const GUARANTEE = {
  NotSubmitted: 1,
  Submitted: 2,
  UnderReview: 3,
  Approved: 4,
  Rejected: 5,
  Expired: 6,
};

const toNum = (value) => {
  if (value == null || value === "") return null;
  if (typeof value === "number") return value;
  const n = Number(value);
  return Number.isFinite(n) ? n : null;
};

const matches = (value, ...candidates) => {
  if (value == null) return false;
  const asNum = toNum(value);
  const asStr = String(value);
  return candidates.some((c) => c === value || c === asNum || c === asStr);
};

export const isSignableStatus = (status) =>
  matches(
    status,
    STATUS.Generated,
    STATUS.PendingContractorSignature,
    STATUS.ApprovedForSignature,
    "Generated",
    "PendingContractorSignature",
    "ApprovedForSignature"
  );

export const isPostContractorSignStatus = (status) =>
  matches(
    status,
    STATUS.ContractorSigned,
    STATUS.PendingSolarentSignature,
    STATUS.Signed,
    STATUS.AwaitingGuarantee,
    STATUS.Active,
    "ContractorSigned",
    "PendingSolarentSignature",
    "Signed",
    "AwaitingGuarantee",
    "Active"
  );

export const isAwaitingGuaranteeStatus = (status) =>
  matches(status, STATUS.Signed, STATUS.AwaitingGuarantee, "Signed", "AwaitingGuarantee");

export const isActiveStatus = (status) =>
  matches(status, STATUS.Active, "Active");

export const isGuaranteeApproved = (guaranteeStatus) =>
  matches(guaranteeStatus, GUARANTEE.Approved, "Approved");

export const isGuaranteeUnderReview = (guaranteeStatus) =>
  matches(
    guaranteeStatus,
    GUARANTEE.Submitted,
    GUARANTEE.UnderReview,
    "Submitted",
    "UnderReview"
  );

/**
 * Friendly guarantee label for contractors (no engine enum names).
 */
export const resolveFriendlyGuaranteeStatus = (contract) => {
  if (!contract) return "ارسال نشده";
  if (isGuaranteeApproved(contract.guaranteeStatus)) return "تأیید شده";
  if (isGuaranteeUnderReview(contract.guaranteeStatus)) return "در حال بررسی";
  if (matches(contract.guaranteeStatus, GUARANTEE.Rejected, "Rejected"))
    return "نیاز به ارسال مجدد";
  if (contract.hasActiveGuaranteeDocument) return "دریافت شده";
  return "ارسال نشده";
};

/**
 * Resolve contractor-facing phase + action + progress from a master contract DTO.
 */
export const resolveContractorContractUx = (contract, options = {}) => {
  const {
    contractsPath = "/SLN/Contractor/contracts",
    projectsPath = "/SLN/Contractor/projects",
  } = options;

  if (!contract?.contractId && !contract?.contractNumber) {
    return {
      phase: CONTRACTOR_CONTRACT_PHASE.EMPTY,
      title: "قرارداد همکاری",
      contractNumber: null,
      friendlyStatus: "هنوز صادر نشده",
      statusHint: "پس از تأیید نهایی، قرارداد اینجا نمایش داده می‌شود.",
      nextRequiredAction: "فعلاً اقدامی لازم نیست",
      action: null,
      progress: {
        completedCount: 0,
        currentIndex: 0,
        steps: CONTRACTOR_PROGRESS_STEPS.map((s, i) => ({
          ...s,
          state: i === 0 ? "current" : "upcoming",
          statusLabel: i === 0 ? "در انتظار شما" : "در انتظار",
        })),
      },
      showGuarantee: false,
      showSignFlow: false,
    };
  }

  const status = contract.status;
  const contractHref = contract.contractId
    ? `${contractsPath}/${contract.contractId}`
    : contractsPath;
  const title = "قرارداد همکاری با سولارنت";
  const contractNumber = contract.contractNumber || "—";

  let phase = CONTRACTOR_CONTRACT_PHASE.UNAVAILABLE;
  let friendlyStatus = "در حال بررسی";
  let statusHint = "وضعیت قرارداد در حال به‌روزرسانی است.";
  let nextRequiredAction = "لطفاً کمی بعد دوباره بررسی کنید";
  let action = null;
  let completedCount = 2;
  let currentIndex = 2;
  let showGuarantee = false;
  let showSignFlow = false;

  if (isActiveStatus(status)) {
    phase = CONTRACTOR_CONTRACT_PHASE.ACTIVE;
    friendlyStatus = "همکاری فعال";
    statusHint = "قرارداد و ضمانت تکمیل شده و دسترسی پروژه‌ها برقرار است.";
    nextRequiredAction = "مشاهده پروژه‌ها";
    action = {
      key: "view_projects",
      title: "همکاری فعال شد",
      description: "می‌توانید پروژه‌های تخصیص‌یافته را از پنل پیگیری کنید.",
      buttonLabel: "مشاهده پروژه‌ها",
      to: projectsPath,
      severity: "success",
    };
    completedCount = 5;
    currentIndex = 4;
    showGuarantee = Boolean(
      contract.hasActiveGuaranteeDocument || contract.canViewGuaranteeDocument
    );
  } else if (isSignableStatus(status) && !isPostContractorSignStatus(status)) {
    phase = CONTRACTOR_CONTRACT_PHASE.PENDING_SIGNATURE;
    friendlyStatus = "آماده امضا";
    statusHint =
      "قبل از امضا تصویر چک را بارگذاری کنید؛ پس از امضا امکان تغییر تصویر وجود ندارد.";
    nextRequiredAction = "امضای قرارداد";
    action = {
      key: "sign_contract",
      title: "قرارداد آماده امضا است",
      description:
        "در صورت نیاز تصویر چک را همین‌جا بارگذاری کنید، سپس متن قرارداد را بخوانید و امضا کنید.",
      buttonLabel: "مشاهده و امضای قرارداد",
      to: contractHref,
      severity: "warning",
    };
    completedCount = 2;
    currentIndex = 2;
    showSignFlow = true;
    showGuarantee = Boolean(
      contract.canUploadGuaranteeDocument ||
        contract.hasActiveGuaranteeDocument ||
        contract.hasGuaranteeChequeDetails ||
        contract.HasGuaranteeChequeDetails ||
        contract.guaranteeSayadiId ||
        contract.GuaranteeSayadiId
    );
  } else if (
    matches(
      status,
      STATUS.ContractorSigned,
      STATUS.PendingSolarentSignature,
      "ContractorSigned",
      "PendingSolarentSignature"
    ) ||
    isAwaitingGuaranteeStatus(status) ||
    Boolean(contract.canUploadGuaranteeDocument)
  ) {
    const physicalReceived = Boolean(
      contract.physicalGuaranteeReceivedAt ||
        contract.PhysicalGuaranteeReceivedAt
    );
    const hasChequeDetails = Boolean(
      contract.hasGuaranteeChequeDetails ||
        contract.HasGuaranteeChequeDetails ||
        contract.guaranteeSayadiId ||
        contract.GuaranteeSayadiId
    );
    const guaranteeRejected = matches(
      contract.guaranteeStatus,
      GUARANTEE.Rejected,
      "Rejected"
    );
    const underReview =
      !guaranteeRejected &&
      (physicalReceived ||
        isGuaranteeUnderReview(contract.guaranteeStatus) ||
        matches(
          status,
          STATUS.PendingSolarentSignature,
          "PendingSolarentSignature"
        ));

    if (underReview) {
      phase = CONTRACTOR_CONTRACT_PHASE.GUARANTEE_REVIEW;
      friendlyStatus = physicalReceived
        ? "در انتظار امضای نهایی سولارنت"
        : "ضمانت در حال بررسی";
      statusHint = physicalReceived
        ? "چک در دفتر دریافت شد. پس از امضای نهایی سولارنت، حساب شما تکمیل می‌شود."
        : "مدرک ضمانت دریافت شده و در حال بررسی است.";
      nextRequiredAction = "منتظر نتیجه بررسی بمانید";
      action = {
        key: "wait_guarantee_review",
        title: physicalReceived
          ? "چک شما دریافت شد"
          : "ضمانت شما دریافت شد",
        description: physicalReceived
          ? "پس از امضای نهایی سولارنت، همکاری و حساب پیمانکار فعال می‌شود."
          : "در صف بررسی مدیر است. پس از تأیید نهایی، همکاری فعال می‌شود.",
        buttonLabel: null,
        to: null,
        severity: "info",
      };
      completedCount = 4;
      currentIndex = 3;
      showGuarantee = true;
    } else if (
      matches(status, STATUS.ContractorSigned, "ContractorSigned") ||
      hasChequeDetails ||
      isPostContractorSignStatus(status)
    ) {
      // After contractor signs: mailing only — cheque image pick is locked.
      phase = CONTRACTOR_CONTRACT_PHASE.AWAITING_GUARANTEE;
      friendlyStatus = "ارسال پستی چک ضمانت";
      statusHint =
        "چک را دقیقاً مطابق مشخصات زیر تکمیل کنید و به آدرس دفتر سولارنت پست نمایید.";
      nextRequiredAction = "ارسال چک به دفتر سولارنت";
      action = {
        key: "mail_physical_guarantee",
        title: "قرارداد شما امضا شد",
        description:
          "اکنون چک ضمانت را مطابق مشخصات قرارداد تکمیل کرده و به آدرس دفتر پست کنید. پس از تأیید دریافت، امضای نهایی انجام می‌شود.",
        buttonLabel: "مشاهده مشخصات و آدرس ارسال",
        tip:
          contract.solarentOfficeMailingAddress ||
          contract.SolarentOfficeMailingAddress ||
          "آدرس ارسال در صفحه قرارداد نمایش داده می‌شود.",
        to: contractHref,
        scrollTo: "guarantee",
        severity: "warning",
      };
      completedCount = 3;
      currentIndex = 3;
      showGuarantee = true;
    } else if (Boolean(contract.canUploadGuaranteeDocument) && guaranteeRejected) {
      phase = CONTRACTOR_CONTRACT_PHASE.AWAITING_GUARANTEE;
      friendlyStatus = "در انتظار بارگذاری چک ضمانت";
      statusHint =
        "اطلاعات چک و تصویر را وارد کنید، سپس ذخیره و ارسال برای بررسی را بزنید.";
      nextRequiredAction = "ذخیره و ارسال چک ضمانت";
      action = {
        key: "upload_guarantee",
        title: "بارگذاری مجدد چک ضمانت",
        description:
          "ضمانت رد شده است. اطلاعات و تصویر جدید چک را وارد کنید و ذخیره کنید.",
        buttonLabel: "ارسال چک ضمانت",
        tip: "پس از انتخاب فایل، حتماً دکمه ذخیره و ارسال برای بررسی را بزنید.",
        to: contractHref,
        scrollTo: "guarantee",
        severity: "warning",
      };
      completedCount = 3;
      currentIndex = 3;
      showGuarantee = true;
    } else {
      phase = CONTRACTOR_CONTRACT_PHASE.AWAITING_GUARANTEE;
      friendlyStatus = "ارسال پستی چک ضمانت";
      statusHint =
        "پس از امضا، تصویر چک قابل تغییر نیست. چک فیزیکی را به دفتر سولارنت پست کنید.";
      nextRequiredAction = "ارسال چک به دفتر سولارنت";
      action = {
        key: "mail_physical_guarantee",
        title: "قرارداد شما امضا شد",
        description:
          "چک ضمانت را مطابق مشخصات قرارداد تکمیل کرده و به آدرس دفتر پست کنید.",
        buttonLabel: "مشاهده مشخصات و آدرس ارسال",
        tip:
          contract.solarentOfficeMailingAddress ||
          contract.SolarentOfficeMailingAddress ||
          "آدرس ارسال در صفحه قرارداد نمایش داده می‌شود.",
        to: contractHref,
        scrollTo: "guarantee",
        severity: "warning",
      };
      completedCount = 3;
      currentIndex = 3;
      showGuarantee = true;
    }
  } else if (
    matches(
      status,
      STATUS.ContractorSigned,
      STATUS.PendingSolarentSignature,
      "ContractorSigned",
      "PendingSolarentSignature"
    )
  ) {
    phase = CONTRACTOR_CONTRACT_PHASE.WAITING_COMPLETION;
    friendlyStatus = "در حال تکمیل";
    statusHint = "امضای شما ثبت شد. در حال تکمیل نهایی قرارداد هستیم.";
    nextRequiredAction = "فعلاً منتظر بمانید";
    action = {
      key: "wait_completion",
      title: "امضای شما ثبت شد",
      description: "به‌زودی مرحله ارسال ضمانت برای شما باز می‌شود.",
      buttonLabel: null,
      to: null,
      severity: "info",
    };
    completedCount = 3;
    currentIndex = 2;
  } else if (
    matches(
      status,
      STATUS.Cancelled,
      STATUS.Superseded,
      STATUS.Expired,
      "Cancelled",
      "Superseded",
      "Expired"
    )
  ) {
    phase = CONTRACTOR_CONTRACT_PHASE.UNAVAILABLE;
    friendlyStatus = "غیرفعال";
    statusHint = "این قرارداد دیگر قابل پیگیری نیست.";
    nextRequiredAction = "اقدامی لازم نیست";
    action = null;
    completedCount = 0;
    currentIndex = 0;
  } else {
    phase = CONTRACTOR_CONTRACT_PHASE.WAITING_COMPLETION;
    friendlyStatus = "در حال آماده‌سازی";
    statusHint =
      "قرارداد در حال آماده‌سازی است. به‌زودی برای امضا در دسترس قرار می‌گیرد.";
    nextRequiredAction = "فعلاً منتظر بمانید";
    action = {
      key: "wait_preparation",
      title: "قرارداد در حال آماده‌سازی است",
      description: "پس از آماده‌شدن، از همین صفحه می‌توانید امضا کنید.",
      buttonLabel: null,
      to: null,
      severity: "info",
    };
    completedCount = 1;
    currentIndex = 1;
  }

  const steps = CONTRACTOR_PROGRESS_STEPS.map((step, index) => {
    let state = "upcoming";
    if (phase === CONTRACTOR_CONTRACT_PHASE.ACTIVE) {
      state = "done";
    } else if (index < completedCount) {
      state = "done";
    } else if (index === currentIndex) {
      state = "current";
    }
    const statusLabel =
      state === "done"
        ? "انجام شد"
        : state === "current"
        ? "در انتظار شما"
        : "در انتظار";
    return { ...step, state, statusLabel };
  });

  return {
    phase,
    title,
    contractNumber,
    friendlyStatus,
    statusHint,
    nextRequiredAction,
    action,
    progress: {
      completedCount,
      currentIndex,
      steps,
    },
    showGuarantee,
    showSignFlow,
    versionNumber: contract.versionNumber,
    createOn: contract.createOn,
    signedAt: contract.signedAt || contract.contractorSignedAt,
    activatedAt: contract.activatedAt,
    downloadUrl: contract.signedPdfUrl || contract.finalPdfUrl || null,
    canDownload: Boolean(contract.signedPdfUrl || contract.finalPdfUrl),
    guaranteeStatusLabel: resolveFriendlyGuaranteeStatus(contract),
    guaranteeAmountIrr: contract.guaranteeAmountIrr,
    guaranteeTypeLabel: "چک صیادی",
    requiredDocumentLabel: "تصویر چک صیادی (رو و پشت)",
    // After contractor sign, lock image pick/upload unless guarantee was rejected.
    canUploadGuarantee: (() => {
      const apiAllows = Boolean(contract.canUploadGuaranteeDocument);
      if (!apiAllows) return false;
      if (!isPostContractorSignStatus(status)) return true;
      return matches(contract.guaranteeStatus, GUARANTEE.Rejected, "Rejected");
    })(),
    hasGuaranteeDocument: Boolean(contract.hasActiveGuaranteeDocument),
    canViewGuarantee: Boolean(contract.canViewGuaranteeDocument),
    guaranteeSayadiId:
      contract.guaranteeSayadiId || contract.GuaranteeSayadiId || null,
    guaranteeChequeSerial:
      contract.guaranteeChequeSerial || contract.GuaranteeChequeSerial || null,
    guaranteeBankName:
      contract.guaranteeBankName || contract.GuaranteeBankName || null,
    guaranteeAccountNumber:
      contract.guaranteeAccountNumber || contract.GuaranteeAccountNumber || null,
    guaranteeBranchName:
      contract.guaranteeBranchName || contract.GuaranteeBranchName || null,
    guaranteeIssuerName:
      contract.guaranteeIssuerName || contract.GuaranteeIssuerName || null,
    guaranteeChequeDateFa:
      contract.guaranteeChequeDateFa || contract.GuaranteeChequeDateFa || null,
    physicalGuaranteeReceivedAt:
      contract.physicalGuaranteeReceivedAt ||
      contract.PhysicalGuaranteeReceivedAt ||
      null,
    solarentOfficeMailingAddress:
      contract.solarentOfficeMailingAddress ||
      contract.SolarentOfficeMailingAddress ||
      null,
    postSignMessage: contract.postSignMessage || contract.PostSignMessage || null,
    contractId: contract.contractId,
  };
};
